/**
 * One configuration row, with who put it there.
 *
 * `data-row` marks the row boundary. It exists so an action can only ever be read, or clicked, in the
 * context of the row it belongs to — the destructive tests scope every query to it, and that scoping is
 * what makes "confirmed the wrong row" a testable failure rather than a hope.
 *
 * Attribution: a seed row reads "configuration", never a blank and never the current admin. Puneet's
 * ask was "log the admin email, maybe not just say that the admin added it" — and the inverse matters
 * as much. A row nobody typed must not appear to have been typed by somebody, or the log accuses a
 * person of a change they did not make.
 */
import type { Source } from '../types';

interface RowProps {
  children: React.ReactNode;
  source: Source;
  createdByEmail: string | null;
  /** Rendered to the right of the attribution — a warning chip, a verdict. */
  note?: React.ReactNode;
  /** The row's actions. */
  actions?: React.ReactNode;
  /** Shown under the row, full width: the confirmation belongs to the row, not to the page. */
  below?: React.ReactNode;
}

export function Row({ children, source, createdByEmail, note, actions, below }: RowProps) {
  return (
    <div data-row className="border-b border-gray-100 py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1 text-sm text-gray-900">{children}</div>
        {note}
        <Attribution source={source} createdByEmail={createdByEmail} />
        {actions}
      </div>
      {below}
    </div>
  );
}

function Attribution({
  source,
  createdByEmail,
}: {
  source: Source;
  createdByEmail: string | null;
}) {
  if (source === 'seed') {
    return (
      <span className="text-xs text-gray-500" title="Written by the seed from configuration files">
        from configuration
      </span>
    );
  }
  // `admin` with no email is the API-key principal, whose id the audit FK cannot honour. Saying so
  // beats an empty cell, which reads as a bug.
  return (
    <span className="text-xs text-gray-600">{createdByEmail || 'added here (actor unknown)'}</span>
  );
}

export default Row;
